import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

import axios from 'axios';
import config from '../../config';

import "../../styles/popup.css"

import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Tooltip from "@mui/material/Tooltip";
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import Autocomplete from '@mui/material/Autocomplete';

const LoadBalanceForm = ({ selectedAccount, currentBalance }) => {

    const queryClient = useQueryClient();

    // Payment Details
    const [accounts, setAccounts] = useState([]);
    const [propertiesFromRoles, setPropertiesFromRoles] = useState([]);
    const [paymentData, setPaymentData] = useState({
        invoiceId: null,
        account: null,
        amount: null,
        paymentMethod: null,
        paymentDate: new Date().toISOString(),
        property: null, // <-- Add this line
    });

    // Card Details
    const [cardNumber, setCardNumber] = useState("");
    const [expMonth, setExpMonth] = useState("");
    const [expYear, setExpYear] = useState("");
    const [cvc, setCvc] = useState("");
    const [message, setMessage] = useState("");

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [activeStep, setActiveStep] = React.useState(0);

    // const newCredit = currentBalance + paymentData.amount;

    // Stepper 

    const steps = [
        'Select pay details',
        'Input pay details',
        'Confirm payment',
    ];

    const properties = propertiesFromRoles
        .map(role => role.property)
        .filter(Boolean);

    const isStepCompleted = (step) => {
        switch (step) {
            case 0:
                return paymentData.account !== null && paymentData.amount !== null && paymentData.paymentMethod !== null;
            case 1:
                return cardNumber !== "" && expMonth !== "" && expYear !== "" && cvc !== "";
            default:
                return true;
        }
    };

    const closePopup = () => {
        setIsOverlayOpen(false);
        setActiveStep(0);
        setPaymentData({
            invoiceId: null,
            account: null,
            amount: null,
            paymentMethod: null,
            paymentDate: new Date().toISOString(),
        });
        setCardNumber("");
        setExpMonth("");
        setExpYear("");
        setCvc("");
    };

    const handleNext = () => {
        if (activeStep === steps.length - 1) {
            // Reset overlay when finished
            setIsOverlayOpen(false);
            setActiveStep(0);

            setPaymentData({
                invoiceId: null,
                account: null,
                amount: null,
                paymentMethod: null,
                paymentDate: new Date().toISOString(),
            });
            setCardNumber("");
            setExpMonth("");
            setExpYear("");
            setCvc("");

        } else {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    // If there is a selected account, set the account id in the payment data
    useEffect(() => {
        if (selectedAccount) {
            setPaymentData((prevData) => ({
                ...prevData,
                account: selectedAccount.value
            }));
        }
    }, [selectedAccount]);

    useEffect(() => {
        if (!selectedAccount) return;
        const fetchAccounts = async () => {
            try {
                const response = await axios.get(`${config.API_URL}/v2/role/propertyRole`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
                });
                setAccounts(response.data);
            } catch (error) {
                console.error('Error fetching accounts:', error);
            }
        };

        fetchAccounts();
    }, [selectedAccount]);

    useEffect(() => {
        const fetchPropertyRoles = async () => {
            if (!paymentData.account) return; // or use selectedAccount.value
            try {
                const response = await axios.get(
                    `${config.API_URL}/v2/role/propertyRole/account/${paymentData.account}`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
                );

                setPropertiesFromRoles(response.data); // adjust if response shape is different
            } catch (error) {
                console.error('Error fetching property roles:', error);
            }
        };
        fetchPropertyRoles();
    }, [paymentData.account]);

    // How much to charge including fees
    const computeChargeAmount = (amount) => {
        if (!amount || isNaN(amount)) return { totalCharge: 0, fee: 0 };

        // totalCharge = (amount + 15) / 0.955
        const totalCharge = (amount + 15) / 0.955;

        // fee = totalCharge - amount
        const fee = totalCharge - amount;

        return { totalCharge, fee };
    };

    const handlePayment = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            if (!paymentData.amount || isNaN(paymentData.amount) || paymentData.amount <= 0) {
                setMessage("Please enter a valid amount.");
                return;
            }

            const { totalCharge } = computeChargeAmount(paymentData.amount);
            const amountInCents = Math.round(totalCharge * 100);
            const accountId = selectedAccount.value;

            // Step 1: Create a Payment Intent
            const intentResponse = await axios.post(`${config.API_URL}/v2/payment/create-payment-intent`, {
                amount: amountInCents, // Amount in cents
                currency: "PHP",
                accountId: accountId,
                property: paymentData.property,
            }, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
            const clientKey = intentResponse.data.data.attributes.client_key;
            const paymentIntentId = intentResponse.data.data.id;

            console.log("Step 1:", intentResponse.data);

            // Step 2: Create Payment Method
            const paymentMethodResponse = await axios.post(
                `${config.PAYMENT_API_URL}/payment_methods`,
                {
                    data: {
                        attributes: {
                            type: "card",
                            details: {
                                card_number: cardNumber,
                                exp_month: parseInt(expMonth),
                                exp_year: parseInt(expYear),
                                cvc,
                            },
                            metadata: {
                                accountId
                            }
                        },
                    },
                },
                {
                    headers: {
                        Authorization: `Basic ${btoa(config.PAYMENT_PUBLIC_KEY)}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const paymentMethodId = paymentMethodResponse.data.data.id;
            console.log("Step 2:", paymentMethodResponse.data);

            // Step 3: Attach Payment Method to Payment Intent
            await axios.post(
                `${config.PAYMENT_API_URL}/payment_intents/${paymentIntentId}/attach`,
                {
                    data: {
                        attributes: {
                            payment_method: paymentMethodId,
                            client_key: clientKey,
                            metadata: {
                                accountId
                            }
                        }
                    }
                },
                {
                    headers: {
                        Authorization: `Basic ${btoa(config.PAYMENT_PUBLIC_KEY)}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            console.log("Step 3: Payment method attached to payment intent");

            setIsOverlayOpen(false);
            toast.success("Loaded balance successfully!");
            queryClient.invalidateQueries(['v2-transactions']); // Still need to reload the page as the transaction list is not updated
        } catch (error) {
            setMessage("Failed to load balance.");
            toast.error("Failed to load balance.");
        } finally {
            setIsSubmitting(false); // re-enable button
        }
    };

    return (
        <div>
            <div className="bt-button flex flex-col h-[80px] md:flex-row md:w-[130px] md:h-[60px]" onClick={() => setIsOverlayOpen(true)}>
                <i class="fas fa-hand-holding-dollar mr-0 md:mr-[15px]"></i>
                <p className="text-center md:text-left mt-[10px] md:mt-0">Load<span className="hidden md:inline"><br /></span> Balance</p>
            </div>
            {isOverlayOpen && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <button className="close-popup" onClick={closePopup} >
                            <i className="fas fa-times"></i>
                        </button>
                        <h2 className="text-2xl text-black">Load Balance</h2>
                        <div>
                            <Stepper activeStep={activeStep} style={{ marginBottom: '30px' }} alternativeLabel>
                                {steps.map((label, index) => {
                                    const stepProps = {};
                                    const labelProps = {};
                                    return (
                                        <Step
                                            key={label}
                                            {...stepProps}
                                            sx={{
                                                "& .MuiStepIcon-root.Mui-active": {
                                                    color: "#C86B2C",
                                                },
                                                "& .MuiStepIcon-root.Mui-completed": {
                                                    color: "#C86B2C",
                                                },
                                            }}>
                                            <StepLabel {...labelProps}>{label}</StepLabel>
                                        </Step>
                                    );
                                })}
                            </Stepper>
                            <div style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                {activeStep === 0 && (
                                    <div>
                                        <Autocomplete
                                            options={accounts}
                                            value={
                                                selectedAccount
                                                    ? { account: { _id: selectedAccount.value, user: { givenName: selectedAccount.label.split(" ")[0], lastName: selectedAccount.label.split(" ").slice(1).join(" ") } } }
                                                    : accounts.find((option) => option.account._id === paymentData.account) || null
                                            }
                                            onChange={(event, newValue) => {
                                                setPaymentData((prevData) => ({
                                                    ...prevData,
                                                    account: newValue ? newValue.account._id : null,
                                                }));
                                            }}
                                            getOptionLabel={(option) =>
                                                option?.account?.user
                                                    ? `${option.account.user.givenName} ${option.account.user.lastName}`
                                                    : ""
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Account"
                                                    variant="outlined"
                                                    fullWidth
                                                    required
                                                />
                                            )}
                                            disabled={Boolean(selectedAccount)}
                                            sx={{ marginBottom: '1rem' }}
                                        />

                                        <Autocomplete
                                            options={properties}
                                            value={properties.find((option) => option._id === paymentData.property) || null}
                                            onChange={(event, newValue) => {
                                                setPaymentData((prevData) => ({
                                                    ...prevData,
                                                    property: newValue ? newValue._id : null,
                                                }));
                                            }}
                                            getOptionLabel={(option) =>
                                                option?.name ? option.name : ""
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Property"
                                                    variant="outlined"
                                                    fullWidth
                                                    required
                                                />
                                            )}
                                            sx={{ marginBottom: '1rem' }}
                                        />
                                        <div className="popup-columns">
                                            <FormControl fullWidth>
                                                <InputLabel htmlFor="outlined-adornment-amount">Amount</InputLabel>
                                                <OutlinedInput
                                                    id="outlined-adornment-amount"
                                                    value={paymentData.amount || ''}
                                                    onChange={(e) =>
                                                        setPaymentData({
                                                            ...paymentData,
                                                            amount: e.target.value ? parseFloat(e.target.value) : null
                                                        })
                                                    }
                                                    type="number"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    startAdornment={<InputAdornment position="start">PHP</InputAdornment>}
                                                    label="Amount"
                                                    sx={{ marginBottom: '1rem' }}
                                                />
                                            </FormControl>
                                            <Autocomplete
                                                options={["Credit Card", "Bank Transfer", "GCash", "Cash"]}
                                                value={paymentData.paymentMethod || null}
                                                onChange={(event, newValue) =>
                                                    setPaymentData({ ...paymentData, paymentMethod: newValue || null })
                                                }
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Payment Method" />
                                                )}
                                                fullWidth
                                                sx={{ marginBottom: '1rem' }}
                                            />
                                        </div>
                                    </div>
                                )}
                                {activeStep === 1 && (
                                    <div>
                                        <p>{message}</p>
                                        <TextField
                                            label="Card Number"
                                            name="cardNumber"
                                            variant="outlined"
                                            fullWidth
                                            value={cardNumber}
                                            onChange={(e) => setCardNumber(e.target.value)}
                                            sx={{ marginBottom: '1rem' }}
                                            required
                                        />
                                        <div className="space-section">
                                            <div className="left" style={{ display: "flex", gap: "15px" }}>
                                                <TextField
                                                    label="MM"
                                                    name="expMonth"
                                                    variant="outlined"
                                                    fullWidth
                                                    value={expMonth}
                                                    onChange={(e) => {
                                                        const numericValue = e.target.value.replace(/\D/g, "");
                                                        setExpMonth(numericValue);
                                                    }}
                                                    type="number"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    sx={{ marginBottom: "1rem" }}
                                                    required
                                                />
                                                <TextField
                                                    label="YY"
                                                    name="expYear"
                                                    variant="outlined"
                                                    fullWidth
                                                    value={expYear}
                                                    onChange={(e) => {
                                                        const numericValue = e.target.value.replace(/\D/g, "");
                                                        setExpYear(numericValue);
                                                    }}
                                                    type="number"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    sx={{ marginBottom: "1rem" }}
                                                    required
                                                />
                                            </div>
                                            <div className="right">
                                                <TextField
                                                    label="CVC"
                                                    name="cvc"
                                                    variant="outlined"
                                                    fullWidth
                                                    value={cvc}
                                                    onChange={(e) => {
                                                        const numericValue = e.target.value.replace(/\D/g, "");
                                                        setCvc(numericValue);
                                                    }}
                                                    type="number"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    sx={{ marginBottom: "1rem" }}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {activeStep === 2 && (
                                    <div>
                                        <h2 className="text-black text-xl" style={{ textAlign: 'left', marginBottom: '15px' }}>Payment</h2>
                                        <table className="form-table">
                                            <colgroup>
                                                <col style={{ width: "65%" }} />
                                                <col style={{ width: "35%" }} />
                                            </colgroup>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <div className="detail-column">
                                                            <p className="detail-label">Account</p>
                                                            <p className="detail">
                                                                {selectedAccount ? `${selectedAccount.label}` : 'N/A'}
                                                            </p>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="detail-column">
                                                            <p className="detail-label">Payment Method</p>
                                                            <p className="detail">{paymentData.paymentMethod}</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <p style={{ fontSize: '0.85rem', color: '#555', marginBottom: '10px' }}>
                                            Fee is calculated as 4.5% of total + ₱15 fixed fee.
                                        </p>
                                        <table className="summary-table" style={{ borderTop: "none" }}>
                                            <colgroup>
                                                <col style={{ width: "65%" }} />
                                                <col style={{ width: "35%" }} />
                                            </colgroup>
                                            <tbody>
                                                {(() => {
                                                    const { totalCharge, fee } = computeChargeAmount(paymentData.amount || 0);
                                                    return (
                                                        <>
                                                            <tr>
                                                                <td>Amount to Load:</td>
                                                                <td>PHP {(paymentData.amount || 0).toFixed(2)}</td>
                                                            </tr>
                                                            <tr>
                                                                <td>Processing Fee:</td>
                                                                <td>PHP {fee.toFixed(2)}</td>
                                                            </tr>
                                                            <tr>
                                                                <td>Total Charge:</td>
                                                                <td>PHP {totalCharge.toFixed(2)}</td>
                                                            </tr>
                                                        </>
                                                    );
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            <React.Fragment>
                                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 4, paddingLeft: '10px', paddingRight: '10px' }}>
                                    <button className="stepper-button" disabled={activeStep === 0} onClick={handleBack}>
                                        Back
                                    </button>
                                    <div className="stepper-message">
                                        <p className="message">{message}</p>
                                    </div>
                                    <Box sx={{ flex: '1 1 auto' }} />
                                    <Tooltip title={!isStepCompleted(activeStep) ? "Please complete this step before proceeding" : ""}>
                                        <span>
                                            <button
                                                className="stepper-button"
                                                onClick={activeStep === steps.length - 1 ? handlePayment : handleNext}
                                                disabled={
                                                    !isStepCompleted(activeStep) ||
                                                    (activeStep === steps.length - 1 && isSubmitting)
                                                }
                                                style={{
                                                    backgroundColor:
                                                        activeStep === steps.length - 1 && isSubmitting
                                                            ? '#d3d3d3' // light grey
                                                            : '',
                                                    color:
                                                        activeStep === steps.length - 1 && isSubmitting
                                                            ? 'black'
                                                            : '',
                                                    cursor:
                                                        activeStep === steps.length - 1 && isSubmitting
                                                            ? 'not-allowed'
                                                            : 'pointer',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                {activeStep === steps.length - 1
                                                    ? isSubmitting
                                                        ? "Processing..."
                                                        : "Finish"
                                                    : "Next"}
                                            </button>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </React.Fragment>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default LoadBalanceForm;